import { PlanAsset } from 'api-validations/locutus'
import { type FC, useEffect, useRef } from 'react'
import { Icon } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { assignZoneIndices, PLAN_ASSET_STYLE, planZoneColor } from 'shared-ui/map'

import {
  type PlanAssetGeometry,
  type PlanAssetPreview,
  planFeatures,
  previewTransform,
  projectRing
} from '../lib/planAssetPreview'

/**
 * Three corners necessary to create a polygon, as x, y pairs.
 */
const MIN_PROJECTED_NUMBERS = 6

const fillsFor = (asset: PlanAssetGeometry, preview: PlanAssetPreview) => {
  if (asset.assetType === PlanAsset.Boundary) {
    const style = preview.inverted ? PLAN_ASSET_STYLE.INVERTED_BOUNDARY : PLAN_ASSET_STYLE.BOUNDARY
    return { fill: () => style.FILL, stroke: style.STROKE }
  }

  // We reuse the zone colors from the map.
  const zones = assignZoneIndices(planFeatures(asset))
  return { fill: (featureIndex: number) => planZoneColor(zones[featureIndex]), stroke: undefined }
}

const draw = ({
  canvas,
  asset,
  preview,
  size
}: {
  canvas: HTMLCanvasElement
  asset: PlanAssetGeometry
  preview: PlanAssetPreview
  size: number
}) => {
  const context = canvas.getContext('2d')
  if (context === null) return

  const pixelRatio = window.devicePixelRatio
  canvas.width = size * pixelRatio
  canvas.height = size * pixelRatio
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  context.clearRect(0, 0, size, size)

  const transform = previewTransform({ bbox: preview.bbox, width: size, height: size })
  const { fill, stroke } = fillsFor(asset, preview)

  context.lineWidth = 1
  if (stroke !== undefined) context.strokeStyle = stroke

  for (const { rings, featureIndex } of preview.polygons) {
    const path = new Path2D()
    for (const ring of rings) {
      const points = projectRing(ring, transform)
      if (points.length < MIN_PROJECTED_NUMBERS) continue
      path.moveTo(points[0], points[1])
      for (let at = 2; at < points.length; at += 2) path.lineTo(points[at], points[at + 1])
      path.closePath()
    }

    context.fillStyle = fill(featureIndex)
    /* The field (outside ring) and any holes (inside rings) share one path. An
    even-odd fill rule leaves inside rings empty while filling the outside ring. */
    context.fill(path, 'evenodd')
    if (stroke !== undefined) context.stroke(path)
  }
}

const frameClasses = createVariants({
  base: 'shrink-0 overflow-hidden rounded border border-gray-200 bg-gray-50'
})

type Props = {
  asset: PlanAssetGeometry
  preview: PlanAssetPreview | undefined
  size: number
}

/**
 * Colored canvas thumbnail displaying an asset's shape.
 */
export const PlanAssetThumbnail: FC<Props> = ({ asset, preview, size }) => {
  const canvas = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (canvas.current === null || preview === undefined) return
    draw({ canvas: canvas.current, asset, preview, size })
  }, [asset, preview, size])

  if (preview === undefined)
    return (
      <div
        className={frameClasses({ className: 'flex items-center justify-center' })}
        style={{ width: size, height: size }}
      >
        <Icon icon={SpriteMap.AlertIcon} className="size-4 text-gray-400" />
      </div>
    )

  return (
    <canvas
      ref={canvas}
      className={frameClasses()}
      style={{ width: size, height: size }}
      aria-hidden
    />
  )
}
